import React, { memo, useCallback, useState } from "react";
import { Story, StoryComment, User } from "@dreambigger/shared/src/types";
import {
  Button,
  Group,
  Avatar,
  Divider,
  Popover,
  Tooltip,
} from "@dreambigger/design-system/src/components/atoms";
import { Comment as AntComment } from "@dreambigger/design-system/src/components/atoms";
import {
  Icon,
  LikeFilled,
  LikeOutlined,
  IconLookup,
} from "@dreambigger/design-system/src/icons";
import {
  faEllipsisV,
  faFlag,
  faTrash,
} from "@dreambigger/design-system/src/icons/light";
import { generateCloudinaryUrl } from "@dreambigger/shared/src/utils";
import { calculateTimePast } from "@dreambigger/shared/src/dates/calculate-days-past";
import { KeyedMutator } from "swr/dist/types";
import { commentApi } from "@dreambigger/shared/src/api/weserve/api-helper";
import { useGA, useMixpanel, useSegment } from "@dreambigger/shared/src/hooks";
import { useRouter } from "next/router";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";

const Comment = ({
  currentUser,
  comment,
  replyEnabled = false,
  children,
  refetchStory,
}: {
  comment: StoryComment;
  currentUser?: User;
  replyEnabled?: boolean;
  children?: React.ReactNode;
  refetchStory: KeyedMutator<Story>;
}) => {
  const [menuLoading, setMenuLoading] = useState(false);
  const [togglingLike, setTogglingLike] = useState(false);
  const createdByCurrentUser = currentUser?.id === comment.user.id;
  const commenterName = comment.user.firstName + " " + comment.user.lastName;
  const commenterInitials = String(
    comment.user.firstName?.[0] + comment.user.lastName?.[0]
  ).toUpperCase();
  const googleAnalytics = useGA();
  const mixpanel = useMixpanel();
  const router = useRouter();
  const segment = useSegment();

  const handleLike = useCallback(() => {
    if (togglingLike) {
      return;
    }

    setTogglingLike(true);

    // Track the like in analytics
    googleAnalytics.event({
      category: "Like",
      action: "Selected",
      label: "Type:comment", // From MT - My best guess for how this was recorded in legacy.
      value: 1,
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.LIKE,
      properties: {
        "Card Type": "Story-Comment", // From MT - Also my best guess for how this might have been recorded.
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
      },
      mixpanel,
      segment,
    });

    commentApi
      .toggleCommentLike(comment.id)
      .then(() => {
        refetchStory().finally(() => setTogglingLike(false));
      })
      .catch(() => setTogglingLike(false));
  }, [comment?.id, togglingLike]);

  const handleReport = useCallback(() => {
    if (menuLoading) {
      return;
    }

    setMenuLoading(true);

    commentApi
      .reportComment(comment.id)
      .then(() => {
        refetchStory().finally(() => setMenuLoading(false));
      })
      .catch(() => setMenuLoading(false));
  }, [comment?.id, togglingLike]);

  const handleDelete = useCallback(() => {
    if (menuLoading) {
      return;
    }

    setMenuLoading(true);

    commentApi
      .deleteComment(comment.id)
      .then(() => {
        refetchStory().finally(() => setMenuLoading(false));
      })
      .catch(() => setMenuLoading(false));
  }, [comment?.id, togglingLike]);

  const showCommentAvatar =
    comment.user.imageId &&
    // We don't want to use the default avatar image that is saved to users.
    comment.user.imageId !== "avatar";

  return (
    <AntComment
      key={comment.id}
      actions={[
        <Group
          xs="horizontal"
          spacing={3}
          className="w-100 items-center"
          key="comment-actions"
        >
          <Tooltip title={!currentUser ? "Sign in to like" : ""}>
            <Button
              aria-label="like button"
              key="like-button"
              disabled={!currentUser}
              loading={togglingLike}
              icon={
                comment.iLiked ? (
                  <LikeFilled className="ml-3" />
                ) : (
                  <LikeOutlined className="ml-3" />
                )
              }
              onClick={handleLike}
              className={`${comment.iLiked && "primary"}`}
              type="text"
            >
              <span className="comment-action ml-2 mr-3 f-2">
                {String(comment.likes)}
              </span>
            </Button>
          </Tooltip>
          {replyEnabled && (
            <Button
              aria-label="reply button"
              type="text"
              key="comment-nested-reply-to"
              className="f-2"
            >
              Reply
            </Button>
          )}
          <Popover
            key="comment-menu"
            content={
              createdByCurrentUser ? (
                <Button
                  aria-label="delete comment"
                  type="text"
                  onClick={handleDelete}
                  icon={<Icon icon={faTrash as IconLookup} className="mr-2" />}
                >
                  Delete Comment
                </Button>
              ) : (
                <Button
                  aria-label="report comment"
                  type="text"
                  onClick={handleReport}
                  icon={<Icon icon={faFlag as IconLookup} className="mr-2" />}
                >
                  Report Comment
                </Button>
              )
            }
            trigger="click"
            placement="bottomRight"
          >
            <Button
              aria-label="more options"
              size="small"
              loading={menuLoading}
              icon={<Icon icon={faEllipsisV as IconLookup} />}
              className="ml-auto"
            />
          </Popover>
        </Group>,
      ]}
      author={commenterName}
      avatar={
        <Avatar
          src={
            showCommentAvatar &&
            generateCloudinaryUrl(
              comment.user.imageVersion,
              comment.user.imageId,
              { type: "avatar" }
            )
          }
          alt={commenterName + " Avatar"}
          style={{ cursor: "default" }}
        >
          {!showCommentAvatar && commenterInitials}
        </Avatar>
      }
      content={<p>{comment.body}</p>}
      datetime={
        <span>
          <Divider type="vertical" className="ml-1 mr-3" />
          {calculateTimePast(new Date(comment.timestamp))}
        </span>
      }
    >
      {children}
    </AntComment>
  );
};

export default memo(Comment);
